import request from '@/utils/request';

/** AI 识别后可回填的选填字段 */
export type AiUploadOptionalFields = {
    website?: string;
    foundedAt?: string;
    email?: string;
    firmSize?: string;
    mainBusiness?: string;
    hotline?: string;
    slogan?: string;
    intro?: string;
    honors?: string;
};

/** AI 识别后可回填的基础信息（必填区） */
export type AiUploadRecognizedBaseFields = {
    companyName?: string;
    address?: string;
    logoData?: string;
};

export type AiUploadResult = {
    /** 上传后的文档访问地址 */
    fileUrl: string;
    fileName?: string;
    /** 识别出的选填字段 */
    fields: AiUploadOptionalFields;
    /** 律所名称、地址、品牌标识 */
    base: AiUploadRecognizedBaseFields;
    promoImages: string[];
    promoVideos: string[];
};

type LawFirmRecognitionPayload = {
    firmId?: string;
    logoUrl?: string;
    firmName?: string;
    address?: string;
    /** AI 识别文档 url */
    recognitionUrl?: string;
    fileName?: string;
    themeColor?: string;
    themeColorValue?: string;
    establishedDate?: string;
    website?: string;
    email?: string;
    lawyerScale?: string;
    introduction?: string;
    mainBusiness?: string;
    honors?: string;
    phone?: string;
    creditCode?: string;
    photos?: string;
    videoUrl?: string;
    expertiseAreas?: string;
    slogan?: string;
    /** 兼容旧结构 */
    fileUrl?: string;
    url?: string;
    name?: string;
    fields?: AiUploadOptionalFields;
};

const SUCCESS_STATUS = '10000';

const LAW_FIRM_CONFIG_FIELD_KEYS = [
    'firmId',
    'logoUrl',
    'firmName',
    'address',
    'recognitionUrl',
    'fileName',
    'themeColor',
    'themeColorValue',
    'establishedDate',
    'website',
    'email',
    'lawyerScale',
    'introduction',
    'mainBusiness',
    'honors',
    'replyStyle',
    'phone',
    'creditCode',
    'photos',
    'videoUrl',
    'expertiseAreas',
    'slogan',
    'colorScheme',
    'companyType',
] as const;

/** 后端常返回字面量 "null" / "[]"，统一视为空 */
export const normalizeApiScalar = (value?: string | null): string | undefined => {
    if (value === null || value === undefined) return undefined;
    const raw = String(value).trim();
    if (!raw) return undefined;
    const lowered = raw.toLowerCase();
    if (lowered === 'null' || lowered === 'undefined' || raw === '[]' || raw === '{}') {
        return undefined;
    }
    return raw;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const parseMaybeJsonObject = (value: unknown): Record<string, unknown> | undefined => {
    if (isPlainObject(value)) return value;
    if (typeof value === 'string') {
        const raw = value.trim();
        if (!raw.startsWith('{') && !raw.startsWith('[')) return undefined;
        try {
            const parsed: unknown = JSON.parse(raw);
            return isPlainObject(parsed) ? parsed : undefined;
        } catch {
            return undefined;
        }
    }
    return undefined;
};

const isLawFirmConfigShape = (record: Record<string, unknown>) =>
    LAW_FIRM_CONFIG_FIELD_KEYS.some((key) => {
        const value = record[key];
        return value !== undefined && value !== null && String(value).trim() !== '';
    });

/** 解析接口返回的宣传图/视频地址（逗号分隔、分号分隔或 JSON 数组字符串） */
export const parseMediaUrlList = (value?: string | string[] | null): string[] => {
    if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter(Boolean);
    }
    const raw = String(value ?? '').trim();
    if (!raw) return [];
    if (raw.startsWith('[')) {
        try {
            const parsed = JSON.parse(raw) as unknown;
            if (Array.isArray(parsed)) {
                return parsed.map((item) => String(item).trim()).filter(Boolean);
            }
        } catch {
            // 非 JSON 数组则按分隔符解析
        }
    }
    return raw
        .split(/[,;]/)
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => normalizeApiScalar(item))
        .filter((item): item is string => Boolean(item));
};

const OPTIONAL_FIELD_KEYS: (keyof AiUploadOptionalFields)[] = [
    'website',
    'foundedAt',
    'email',
    'firmSize',
    'mainBusiness',
    'hotline',
    'slogan',
    'intro',
    'honors',
];

const pickOptionalFields = (payload: LawFirmRecognitionPayload): AiUploadOptionalFields => {
    const nested = payload.fields ?? {};
    const result: AiUploadOptionalFields = {};

    const source: Record<string, string | undefined> = {
        website: nested.website ?? payload.website,
        foundedAt: nested.foundedAt ?? payload.establishedDate,
        email: nested.email ?? payload.email,
        firmSize: nested.firmSize ?? payload.lawyerScale,
        mainBusiness: nested.mainBusiness ?? payload.mainBusiness,
        hotline: nested.hotline ?? payload.phone,
        slogan: nested.slogan ?? payload.slogan,
        intro: nested.intro ?? payload.introduction,
        honors: nested.honors ?? payload.honors,
    };

    OPTIONAL_FIELD_KEYS.forEach((key) => {
        const value = normalizeApiScalar(source[key]);
        if (value) {
            result[key] = value;
        }
    });

    return result;
};

const pickAiRecognizedBaseFields = (payload: LawFirmRecognitionPayload): AiUploadRecognizedBaseFields => ({
    companyName: normalizeApiScalar(payload.firmName),
    address: normalizeApiScalar(payload.address),
    logoData: normalizeApiScalar(payload.logoUrl),
});

const pickAiRecognitionFromPayload = (payload: LawFirmRecognitionPayload) => ({
    fields: pickOptionalFields(payload),
    base: pickAiRecognizedBaseFields(payload),
    promoImages: parseMediaUrlList(payload.photos),
    promoVideos: parseMediaUrlList(payload.videoUrl),
});

const extractLawFirmRecognitionPayload = (
    response: Record<string, unknown>,
): LawFirmRecognitionPayload => {
    const inner = parseMaybeJsonObject(response.data);
    if (inner) {
        return inner as LawFirmRecognitionPayload;
    }
    if (isLawFirmConfigShape(response)) {
        return response as LawFirmRecognitionPayload;
    }
    return {};
};

const normalizeAiUploadResponse = (
    raw: LawFirmRecognitionPayload,
    file: File,
): AiUploadResult => {
    const recognized = pickAiRecognitionFromPayload(raw);
    return {
        fileUrl:
            normalizeApiScalar(raw.recognitionUrl)
            || normalizeApiScalar(raw.fileUrl)
            || normalizeApiScalar(raw.url)
            || '',
        fileName: normalizeApiScalar(raw.fileName) || normalizeApiScalar(raw.name) || file.name,
        ...recognized,
    };
};

/**
 * 律所 AI 识别：上传介绍文档并识别选填字段
 * POST /lawFirm/lawFirmRecognition multipart/form-data: file
 */
export async function aiUpload(file: File): Promise<AiUploadResult> {
    const formData = new FormData();
    formData.append('file', file);

    const response = (await request.post('/lawFirm/lawFirmRecognition', formData)) as {
        success?: boolean;
        status?: string;
        msg?: string;
        data?: LawFirmRecognitionPayload;
    };
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || 'AI 识别失败');
    }

    const payload = extractLawFirmRecognitionPayload(response);
    return normalizeAiUploadResponse(payload, file);
}

export const pickAiUploadFormPatch = (fields: AiUploadOptionalFields) => {
    const patch: AiUploadOptionalFields = {};
    OPTIONAL_FIELD_KEYS.forEach((key) => {
        const value = normalizeApiScalar(fields[key]);
        if (value) {
            patch[key] = value;
        }
    });
    return patch;
};

/** 更新律所配置（生成链接/二维码前提交） */
export type UpdateComponyInfoParams = {
    companyId: string;
    companyName: string;
    address: string;
    logoUrl?: string;
    intro?: string;
    hotline?: string;
    website?: string;
    email?: string;
    firmSize?: string;
    mainBusiness?: string;
    honors?: string;
    promoVideos?: string[];
    promoImages?: string[];
    slogan?: string;
    foundedAt?: string;
    themeColor?: string;
    /** 律师填报页链接 */
    shareUrl: string;
    layoutId: string;
    themeId: string;
    cardStyle: string;
    cardPalette: string;
    cardImage: boolean;
    cardAddress: boolean;
    cardWebsite: boolean;
    posterStyle: string;
    posterAddress: boolean;
    posterImage: boolean;
    posterWebsite: boolean;
    posterSlogan: boolean;
    posterIntro: boolean;
    posterFirmFields: boolean;
    /** 名片预览图（PNG Base64 Data URL） */
    cardImageUrl: string;
};

export type UpdateComponyInfoResult = {
    shareUrl?: string;
    url?: string;
};

type UpdateComponyInfoResponse = {
    status?: string;
    msg?: string;
    data?: UpdateComponyInfoResult;
};

/**
 * 更新公司详情
 * POST /updateComponyInfo
 */
/** 上传文件至 OSS（含抠图），lawUser 返回 mattingUrl 供名片/海报预览 */
export type UploadOssAndMattingResult = {
    fileUrl: string;
    mattingUrl?: string;
};

export async function uploadOssAndMatting(
    file: File,
    folderName: 'lawFirm' | 'lawUser',
): Promise<UploadOssAndMattingResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderName', folderName);

    const response = (await request.post('/aliyunOss/uploadOssAndMatting', formData)) as {
        success?: boolean;
        status?: string;
        msg?: string;
        data?: string | { fileUrl?: string; mattingUrl?: string };
    };
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || '上传失败');
    }

    const data = response.data;
    if (typeof data === 'string') {
        const fileUrl = data.trim();
        if (!fileUrl) {
            throw new Error('上传成功但未返回文件地址');
        }
        return { fileUrl };
    }

    const fileUrl = typeof data?.fileUrl === 'string' ? data.fileUrl.trim() : '';
    if (!fileUrl) {
        throw new Error('上传成功但未返回文件地址');
    }
    const mattingUrl = typeof data?.mattingUrl === 'string' ? data.mattingUrl.trim() : undefined;
    return { fileUrl, mattingUrl: mattingUrl || undefined };
}

/** 上传律所 LOGO 至 OSS（含抠图），返回 fileUrl / mattingUrl */
export async function uploadOssAndFirmLogoMatting(
    file: File,
    options?: { skipErrorHandler?: boolean },
): Promise<UploadOssAndMattingResult> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderName', 'lawFirm');

    const response = (await request.post('/aliyunOss/uploadOssAndFirmLogoMatting', formData, {
        skipErrorHandler: options?.skipErrorHandler,
    })) as {
        success?: boolean;
        status?: string;
        msg?: string;
        data?: { fileUrl?: string; mattingUrl?: string };
    };
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || '上传失败');
    }

    const data = response.data;
    const fileUrl = typeof data?.fileUrl === 'string' ? data.fileUrl.trim() : '';
    if (!fileUrl) {
        throw new Error('上传成功但未返回文件地址');
    }
    const mattingUrl = typeof data?.mattingUrl === 'string' ? data.mattingUrl.trim() : undefined;
    return { fileUrl, mattingUrl: mattingUrl || undefined };
}

type UploadOssOptions = {
    skipErrorHandler?: boolean;
};

/** 上传文件至 OSS，返回文件 URL */
export async function uploadOss(
    file: File,
    folderName: 'lawFirm' | 'lawUser' | 'lawAiArticle' | 'modelKnowledge',
    options?: UploadOssOptions,
): Promise<string> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('folderName', folderName);

    const response = (await request.post('/aliyunOss/uploadOss', formData, {
        skipErrorHandler: options?.skipErrorHandler,
    })) as {
        success?: boolean;
        status?: string;
        msg?: string;
        data?: string;
    };
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || '上传失败');
    }

    const url = typeof response.data === 'string' ? response.data.trim() : '';
    if (!url) {
        throw new Error('上传成功但未返回文件地址');
    }
    return url;
}

export type GetLawFirmUsersPageParams = {
    firmId: string;
    keyword?: string;
    pageNum?: number;
    pageSize?: number;
};

export type LawFirmUserRecord = {
    userPicUrl?: string;
    userName?: string;
    userPhone?: string;
    createTime?: string;
    domainList?: string[];
    userJob?: string;
    updateTime?: string;
    isAIAgent?: boolean;
};

export type GetLawFirmUsersPageResult = {
    total?: number;
    pages?: number;
    pageNum?: number;
    records?: LawFirmUserRecord[];
};

/** 律所律师用户分页列表 */
export async function getLawFirmUsersPage(params: GetLawFirmUsersPageParams) {
    return request.post('/lawFirm/getLawFirmUsersPage', {
        data: params,
    });
}

export type GetLawFirmUsersListParams = {
    firmId: string;
    keyword?: string;
};

/** 律所律师用户列表（支持关键词搜索） */
export async function getLawFirmUsersList(params: GetLawFirmUsersListParams) {
    return request.post('/lawFirm/getLawFirmUsersList', {
        data: params,
    });
}

/** 律所基础信息 */
export type LawFirmConfigDO = {
    firmId?: string;
    logoUrl?: string;
    firmName?: string;
    address?: string;
    recognitionUrl?: string;
    /** AI 识别上传的原始文件名 */
    fileName?: string;
    themeColor?: string;
    themeColorValue?: string;
    establishedDate?: string;
    website?: string;
    email?: string;
    lawyerScale?: string;
    introduction?: string;
    mainBusiness?: string;
    /** 详情回参为换行分隔字符串，提交时为字符串数组 */
    honors?: string | string[];
    /** 智能体回复风格：1 简洁版 / 2 专业版 */
    replyStyle?: string;
    phone?: string;
    creditCode?: string;
    photos?: string;
    videoUrl?: string;
    expertiseAreas?: string;
    slogan?: string;
    /** 七大色系：红 / 橙 / 黄 / 绿 / 青 / 蓝 / 紫（保存时优先使用，旧 themeColor 字段仅兼容读取） */
    colorScheme?: string;
    /** 1 为律所，其余为通用企业 */
    companyType?: number | string;
};

/** 名片/海报模版信息（不传 templateId） */
export type LawFirmTemplateDO = {
    styleId?: string;
    colorId?: string;
    /** 0: 律所模版 1: 律师模版 */
    templateType?: number;
};

/** 详情接口返回的模版信息（含 templateId） */
export type LawFirmTemplateDetailDO = LawFirmTemplateDO & {
    templateId?: string;
};

export type GetLawFirmDetailResult = {
    /** 二维码图片 url */
    data?: string;
    /** 已绑定的官方智能体名片 ID */
    bindAgentCardId?: string;
    lawFirmConfigDO?: LawFirmConfigDO;
    cardTemplateDO?: LawFirmTemplateDetailDO;
    posterTemplateDO?: LawFirmTemplateDetailDO;
    digitalCardDTO?: UpdateLawFirmConfigParams['digitalCardDTO'];
};

export type UpdateLawFirmConfigParams = {
    firmId: string;
    lawFirmConfigDO: LawFirmConfigDO;
    cardTemplateDO: LawFirmTemplateDO;
    posterTemplateDO: LawFirmTemplateDO;
    digitalCardDTO?: {
        /** 官方智能体头像 */
        userPicUrl?: string;
        /** 官方智能体名称 */
        userName?: string;
        /** 官方智能体手机号 */
        userPhone?: string;
        /** 官方智能体擅长领域 */
        domainList?: string[];
        /** 官方智能体名片 url */
        digitalCardShareUrl?: string;
        /** 官方智能体海报 url */
        transmitStyleUrl?: string;
        /** 官方智能体个人知识库 */
        modelKnowledge?: string | string[];
        /** 官方名片二维码 */
        wxQrCode?: string;
    };
};

type LawFirmDetailApiData = {
    firmId?: string | null;
    /** 二维码图片 url */
    qrCodeUrl?: string | null;
    data?: string | null;
    lawFirmConfigDO?: LawFirmConfigDO | null;
    cardTemplateDO?: LawFirmTemplateDetailDO | null;
    posterTemplateDO?: LawFirmTemplateDetailDO | null;
    digitalCardDTO?: UpdateLawFirmConfigParams['digitalCardDTO'] | null;
};

const pickLawFirmConfigFromRecord = (raw: Record<string, unknown>): LawFirmConfigDO => {
    const config: LawFirmConfigDO = {};
    LAW_FIRM_CONFIG_FIELD_KEYS.forEach((key) => {
        const rawValue = raw[key as string];
        if (key === 'honors' && Array.isArray(rawValue)) {
            const items = rawValue.map((item) => String(item).trim()).filter(Boolean);
            if (items.length > 0) {
                (config as Record<string, unknown>)[key] = items;
            }
            return;
        }
        const normalized = normalizeApiScalar(rawValue as string | undefined);
        if (normalized !== undefined) {
            (config as Record<string, unknown>)[key] = normalized;
        }
    });
    return config;
};

const mergeLawFirmConfigDO = (
    ...sources: Array<LawFirmConfigDO | undefined | null>
): LawFirmConfigDO | undefined => {
    const merged = Object.assign({}, ...sources.filter(Boolean)) as LawFirmConfigDO;
    return Object.keys(merged).length ? merged : undefined;
};

const resolveLawFirmDetailFromResponse = (
    response: Record<string, unknown>,
): GetLawFirmDetailResult => {
    const innerData = parseMaybeJsonObject(response.data);
    const configBlob: Record<string, unknown> =
        innerData
        ?? (isLawFirmConfigShape(response) ? response : {});

    const nestedConfig = isPlainObject(configBlob.lawFirmConfigDO)
        ? (configBlob.lawFirmConfigDO as Record<string, unknown>)
        : undefined;

    const lawFirmConfigDO = mergeLawFirmConfigDO(
        pickLawFirmConfigFromRecord(configBlob),
        nestedConfig ? pickLawFirmConfigFromRecord(nestedConfig) : undefined,
        pickLawFirmConfigFromRecord(response),
        isPlainObject(response.lawFirmConfigDO)
            ? pickLawFirmConfigFromRecord(response.lawFirmConfigDO as Record<string, unknown>)
            : (response.lawFirmConfigDO as LawFirmConfigDO | undefined),
    );

    const shareUrl =
        (typeof response.data === 'string' ? normalizeApiScalar(response.data) : undefined)
        || normalizeApiScalar(configBlob.qrCodeUrl as string | undefined)
        || normalizeApiScalar(response.qrCodeUrl as string | undefined);

    const cardTemplateDO =
        (configBlob.cardTemplateDO as LawFirmTemplateDetailDO | undefined)
        ?? (response.cardTemplateDO as LawFirmTemplateDetailDO | undefined);
    const posterTemplateDO =
        (configBlob.posterTemplateDO as LawFirmTemplateDetailDO | undefined)
        ?? (response.posterTemplateDO as LawFirmTemplateDetailDO | undefined);
    const digitalCardDTO =
        (configBlob.digitalCardDTO as UpdateLawFirmConfigParams['digitalCardDTO'] | undefined)
        ?? (response.digitalCardDTO as UpdateLawFirmConfigParams['digitalCardDTO'] | undefined);

    const bindAgentCardId =
        normalizeApiScalar(
            configBlob.bindAgentCardId == null ? undefined : String(configBlob.bindAgentCardId),
        )
        ?? normalizeApiScalar(
            response.bindAgentCardId == null ? undefined : String(response.bindAgentCardId),
        );

    return {
        data: shareUrl,
        bindAgentCardId,
        lawFirmConfigDO,
        cardTemplateDO,
        posterTemplateDO,
        digitalCardDTO,
    };
};

/**
 * 获取律所配置详情
 * POST /lawFirm/getLawFirmDetail
 */
export async function getLawFirmDetail(firmId: string): Promise<GetLawFirmDetailResult> {
    const response = (await request.post('/lawFirm/getLawFirmDetail', {
        data: { firmId },
    })) as Record<string, unknown>;

    return resolveLawFirmDetailFromResponse(response);
}

export type GenerateLawFirmQrCodeParams = {
    /** 二维码链接 */
    url: string;
    /** logo 图 url */
    logoUrl: string;
};

/**
 * 生成律所填报二维码
 * POST /lawFirm/generateQrCode
 */
export async function generateLawFirmQrCode(params: GenerateLawFirmQrCodeParams): Promise<string> {
    const response = (await request.post('/lawFirm/generateQrCode', {
        data: params,
    })) as {
        success?: boolean;
        status?: string;
        msg?: string;
        data?: string;
    };
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || '生成二维码失败');
    }
    const imageUrl = typeof response.data === 'string' ? response.data.trim() : '';
    if (!imageUrl) {
        throw new Error('生成二维码成功但未返回图片地址');
    }
    return imageUrl;
}

/**
 * 更新律所配置
 * POST /lawFirm/updateLawFirmConfig
 */
export async function updateLawFirmConfig(params: UpdateLawFirmConfigParams): Promise<string> {
    const response = (await request.post('/lawFirm/updateLawFirmConfig', {
        data: params,
    })) as {
        success?: boolean;
        status?: string;
        msg?: string;
        data?: string;
    };
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || '保存配置失败');
    }
    return typeof response.data === 'string' ? response.data : '';
}

export type BindLawFirmOfficialAgentParams = {
    firmId: string;
    userPhone: string;
};

/**
 * 绑定律所官方智能体账号
 * POST /admin/bindOfficialAgent
 */
export async function bindLawFirmOfficialAgent(params: BindLawFirmOfficialAgentParams): Promise<void> {
    const response = (await request.post('/aiArticle/admin/bindOfficialAgent', {
        data: params,
    })) as {
        success?: boolean;
        status?: string;
        msg?: string;
    };
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || '绑定官方智能体账号失败');
    }
}

/** 模版可选字段配置 */
export type UserDetailFieldItem = {
    sysID?: number;
    userId?: string;
    fieldKey: string;
    fieldLabel: string;
    fieldType: 'cardOptional' | 'posterOptional' | 'baseInfo' | 'personInfo' | 'otherInfo';
    visible: boolean;
    sortOrder: number;
    createdTime?: string;
    updatedTime?: string;
    creator?: string;
    modifier?: string;
    isDeleted?: string;
};

export type LawFirmTemplateWithFieldsDO = LawFirmTemplateDO & {
    sysID?: number;
    templateId?: string;
    fixedField?: string[];
    optionalField?: UserDetailFieldItem[];
    createdTime?: string;
    updatedTime?: string;
    creator?: string;
    modifier?: string;
    isDeleted?: string;
};

/** 模板目录里的可选信息，visible 不参与勾选 */
export type CardTemplateCatalogField = {
    fieldKey: string;
    fieldLabel: string;
    visible?: boolean;
};

export type CardTemplateCatalogBackground = {
    colorId: string;
    thumbnailUrl?: string;
};

export type CardTemplateCatalogItem = {
    styleId: string;
    vip?: boolean;
    coverUrl?: string;
    backgroundList?: CardTemplateCatalogBackground[];
    optionalField?: CardTemplateCatalogField[];
};

export type UserCardInfoDO = {
    sysid?: number;
    cardId?: string;
    userId?: string;
    userName?: string;
    userPhone?: string;
    userJob?: string;
    domain?: string;
    proOrganization?: string;
    companyAddress?: string | null;
    userMail?: string | null;
    userWx?: string | null;
    individualHonour?: string | null;
    workExperienceList?: string[];
    userPicUrl?: string;
    originalPicUrl?: string;
    proIdCard?: string;
    companyId?: string;
    phoneShow?: number;
    mailShow?: number;
    wxShow?: number;
    addressShow?: number;
    cardType?: number;
    cardStatus?: string;
    personalState?: string;
    /** 微信扫码咨询二维码图片地址 */
    wxQrCode?: string;
    [key: string]: unknown;
};

export type CreateCardByLawUserParams = {
    firmId: string | null;
    userPhone: string;
    lawFirmConfigDO: LawFirmConfigDO;
    userCardInfo: UserCardInfoDO;
    cardTemplateDO: LawFirmTemplateWithFieldsDO;
    posterTemplateDO: LawFirmTemplateWithFieldsDO;
    allCardTemplateList?: CardTemplateCatalogItem[];
    userDetailField: {
        baseInfo: UserDetailFieldItem[];
    };
};

/**
 * 律师创建名片
 * POST /lawUser/createCardBylawUser
 * @returns 接口 data 字段，通常为小程序二维码图片地址
 */

// 发送验证码
export async function createCardByLawUser(params: any) {
    return request.post("/lawUser/createCardBylawUser", {
        data: params,
    });
}


export type GetLawUserDetailParams = {
    firmId: string;
    userPhone?: string;
};

const normalizeLawUserDetail = (raw: unknown): CreateCardByLawUserParams => {
    if (!raw || typeof raw !== 'object') {
        throw new Error('配置详情为空');
    }
    return raw as CreateCardByLawUserParams;
};

/** 律师关键词项（如擅长领域） */
export type LawUserKeyword = {
    keywordId: string;
    keywordValue: string;
    keywordContent: string;
    keywordType: string;
};

export type GetLawUserKeywordsParams = {
    /** 字典类型，擅长领域为 "4" */
    keywordType: string;
};

/**
 * 获取律师关键词列表
 * POST /lawUser/getKeywords
 */
export async function getLawUserKeywords(params: GetLawUserKeywordsParams): Promise<LawUserKeyword[]> {
    const response = (await request.post('/lawUser/getKeywords', {
        data: params,
    })) as {
        success?: boolean;
        status?: string;
        msg?: string;
        data?: LawUserKeyword[];
    };
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || '获取专业领域失败');
    }
    return Array.isArray(response.data) ? response.data : [];
}

/**
 * 获取律师配置详情
 * POST /lawUser/getLawUserDetail
 */
export async function getLawUserDetail(params: GetLawUserDetailParams): Promise<CreateCardByLawUserParams> {
    const response = (await request.post('/lawUser/getLawUserDetail', {
        data: params,
    })) as {
        success?: boolean;
        status?: string;
        msg?: string;
        data?: CreateCardByLawUserParams;
    };
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || '获取配置详情失败');
    }
    if (!response.data) {
        throw new Error('配置详情为空');
    }
    return normalizeLawUserDetail(response.data);
}

export async function updateComponyInfo(params: UpdateComponyInfoParams): Promise<UpdateComponyInfoResult> {
    const response = (await request.post('/updateComponyInfo', {
        data: params,
    })) as UpdateComponyInfoResponse;
    if (String(response.status) !== SUCCESS_STATUS) {
        throw new Error(response.msg || '保存公司信息失败');
    }
    return (response.data ?? {}) as UpdateComponyInfoResult;
}
